/* eslint-disable no-unused-vars */
import ChatMessage from "@/models/ChatMessage";
import DateConverter from "@/models/DateConverter";
import DateOptions from "@/models/DateOptions";
import Node from "@/models/Node";
import ProjectServer from "@/models/ProjectServer";
import StringPacking from "@/models/StringPacking";
import Tag from "@/models/Tag";
import Mutations from "@/store/mutations";
import store from "@/store/store";
import { TimelineSyncHandlersAsync, TimelineSyncNodesResponse, TimelineSyncResponsePayload, TimelineSyncTimelineTags } from "./generated/TimelineSync";

class TimelineSyncRequestHandler 
{
    private nodetags = new Map<number, number[]>();
    private relatives = new Map<number, any[]>();

    private handleNode(data: TimelineSyncNodesResponse) 
    {
        const nodeid: number = data.nodeid;
        let node: Node | null = null;

        let nodeName: string = data.name;
        const typeChar: string = nodeName.substring(0, 1);
        nodeName = nodeName.substring(1);

        const color: any = (data.color != null && data.color.r != null) ? data.color : null;
        node = store.getters.nodes.get(nodeid);
        if (node == undefined) 
        {
            node = new Node(nodeid, DateConverter.serverInputToDate(data.start), color);
        }
        else 
        {
            node.nodeid = nodeid;
            node.dateStart = DateConverter.serverInputToDate(data.start);
            if (color == null)
                node.computeEraColor();
            else
                node.color = color;
        }

        node.name = nodeName;
        node.type = (Node.charToType(typeChar) == Node.NodeType.TimelineEra) ? Node.NodeType.TimelineEra : Node.NodeType.TimelineEvent;

        node.important = (data.important == 1);

        const tup = StringPacking.Split(data.description);
        node.description = tup[0];
        if (tup.length > 1) 
        {
            node.references = tup[1];
        }
        if (data.image != undefined && data.image != null && data.image != "") 
        {
            // TODO: URI verification
            node.image = data.image;
        }
        if (data.video != undefined && data.video != null && data.video != "") 
        {
            // TODO: URI verification
            node.video = data.video;
        }
        if (data.imageCaption != null) 
        {
            node.imageCaption = data.imageCaption;
        }
        if (data.audio != undefined && data.audio != null && data.audio != "") 
        {
            console.log(`node ${node.nodeid} reading audio => ${data.audio}`);
            node.audio = data.audio;
        }

        node.dateEnd = DateConverter.serverInputToDate(data.end);
        node.dateOptions = new DateOptions(data.dateOptions);

        node.data = data.data;

        if (this.relatives.has(node.nodeid)) 
        {
            console.log("Warning: Overwriting relatives for node " + node.nodeid);
        }
        this.relatives.set(node.nodeid, data.connections);

        node.tags = [];
        this.nodetags.set(node.nodeid, data.tags);

        node.data = JSON.parse(data.data);

        store.commit(Mutations.setNode, node);
    }

    private handleTimelineTags(data: TimelineSyncTimelineTags) 
    {
        const timelineid = data.timelineid;
        store.commit(Mutations.timelineSyncTimelineTags, data);
    }

    handleRequest(res: TimelineSyncResponsePayload) 
    {
        for (const data of res.tags) 
        {
            const tag = new Tag(data.tagid, data.name, data.type, data.color);
            store.commit(Mutations.setTag, tag);
        }

        for (const data of res.nodes) 
        {
            this.handleNode(data);
        }

        this.handleTimelineTags(res.timelineTags);

        for (const data of res.timelineNodes) 
        {
            store.commit(Mutations.addTimelineNode, data);
        }

        store.commit(Mutations.timelineSyncNodesPostProcess, {
            relatives: this.relatives,
            nodetags: this.nodetags
        });

        for (const data of res.eventOrderings) 
        {
            store.commit(Mutations.setTimelineEventOrdering, data);
        }

        for (const data of res.chatMessages) 
        {
            const msg = new ChatMessage;
            msg.channel = data.channel;
            msg.sender = data.sender;
            msg.senderId = data.senderId;
            msg.timestamp = data.timestamp;
            msg.text = data.text;
            msg.messageid = data.messageid;
            store.commit(Mutations.chatMessage, msg);
        }

        store.commit(Mutations.setAccessedTimelines, res.accessedTimelines);
    }
}

export default class TimelineSyncRequests 
{
    static activeRequest: Promise<TimelineSyncResponsePayload> | null = null;
    static async SyncTimeline(id: number) 
    {
        // If another request is running, wait for it to complete
        if (TimelineSyncRequests.activeRequest) 
        {
            try 
            {
                await TimelineSyncRequests.activeRequest;
            }
            catch (err) 
            {
                // Ignore, assume it's handled elsewhere
            }
        }

        // Early out if the timeline is already synced
        if (((store.getters as any).accessedTimelines as number[]).includes(id)) return;

        const promise = TimelineSyncHandlersAsync.SyncTimeline(id);
        TimelineSyncRequests.activeRequest = promise;

        // This will throw on a server side error
        const res = await promise;
        new TimelineSyncRequestHandler().handleRequest(res);
    }
}